import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { finalize, shareReplay, tap } from 'rxjs/operators';
import { environment } from './../../environments/environment';
import { StorageService } from './storage.service';

const API_URL = environment.API_URL


const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' })
};

interface AuthResponse {
  token: string;
  refreshToken: string;
}

@Injectable({
  providedIn: 'root'
})

export class AuthService {
  private refreshRequest?: Observable<AuthResponse>;
  private refreshTimer?: number;

  constructor(private http: HttpClient, private storageService: StorageService) { }

  login(email: string, password: string): Observable<any> {
    const API_URL_AUTH = API_URL + '/auth/login';
    return this.http.post(API_URL_AUTH, { email, password, }, httpOptions);
  }

  refreshSession(): Observable<AuthResponse> {
    if (this.refreshRequest) return this.refreshRequest;

    const refreshToken = this.storageService.getRefreshToken();
    if (!refreshToken) return throwError(() => new Error('No refresh token is available.'));

    this.refreshRequest = this.http.post<AuthResponse>(`${API_URL}/auth/refresh`, { refreshToken }, httpOptions).pipe(
      tap(response => this.storageService.saveUser(response)),
      finalize(() => this.refreshRequest = undefined),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    return this.refreshRequest;
  }

  scheduleRefresh(): void {
    this.cancelScheduledRefresh();
    const expiresAt = this.storageService.getAccessTokenExpiresAt();
    if (!expiresAt) return;

    const delay = Math.max(0, expiresAt - Date.now() - 60_000);
    if (delay > 2_147_483_647) return;

    this.refreshTimer = window.setTimeout(() => {
      this.refreshSession().subscribe({
        next: () => this.scheduleRefresh(),
        error: () => this.clearSession(),
      });
    }, delay);
  }

  clearSession(): void {
    this.cancelScheduledRefresh();
    this.storageService.clean();
  }

  private cancelScheduledRefresh(): void {
    if (this.refreshTimer !== undefined) window.clearTimeout(this.refreshTimer);
    this.refreshTimer = undefined;
  }

}
